import type { SerializedError } from '@reduxjs/toolkit';
import logError from '@alltrails/core/utils/logError';
import type { PageUI } from '@alltrails/layout/utils/PageUI/pageUI';
import { baseApi, type BaseError } from '@alltrails/redux-helpers/baseApi';
import type { Coordinates } from '@alltrails/types/AlgoliaResults';

export const askAllTrailsUrl = '/api/alltrails/agents/v1/ask-alltrails';

export type AskLocation = { topRight: Coordinates; bottomLeft: Coordinates } | { center: Coordinates; maxRadius?: number };

export type AskAllTrailsRequest = {
  /** The user's text, unmodified. 1–1000 characters. */
  query: string;
  /** The viewport when the question was submitted. The server searches here unless the query names a place. */
  location?: AskLocation;
};

/**
 * One cache entry per submitted question. `requestId` is never sent; it keeps each submission its own request, so asking
 * the same question again (or retrying one that failed) fetches instead of reusing the old response.
 */
export type AskAllTrailsArgs = {
  requestId: string;
  request: AskAllTrailsRequest;
};

export const askAllTrailsApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    askAllTrails: builder.query<PageUI, AskAllTrailsArgs>({
      query: ({ request }) => ({ url: askAllTrailsUrl, method: 'post', data: request }),
      keepUnusedDataFor: 300,
      async onQueryStarted(_args, { queryFulfilled }) {
        await queryFulfilled.catch(rejection => {
          const status = rejection.isUnhandledError ? undefined : rejection.error.status;

          if (status !== 500 && status !== 504) {
            logError(new Error(`Ask AllTrails request failed with status ${status ?? 'unknown'}`, { cause: rejection.error }));
          }
        });
      }
    })
  })
});

export const { useAskAllTrailsQuery } = askAllTrailsApi;

/**
 * The body of a 500 or 504 error.
 */
type AskAllTrailsServerErrorBody = {
  error: { type: 'agent_failure' | 'timeout'; message: string };
};

export type AskAllTrailsError = {
  status?: number;
  message?: string;
  canRetry: boolean;
};

export const getAskAllTrailsError = (error: BaseError | SerializedError): AskAllTrailsError => {
  if (!('status' in error)) {
    return { canRetry: true };
  }

  const { error: serverError }: Partial<AskAllTrailsServerErrorBody> = error.data ?? {};

  return { status: error.status, message: serverError?.message, canRetry: error.status >= 500 };
};
